import { Card, Row, Skeleton } from '../ui'
import { useTrains } from '../lib/transit'
import {
  TRAIN_STOPS, TRAIN_ZONE, atStop, cardView, isLive, lateWord, planLines, reachesFromNorthStation, serviceDay,
  serviceGap, trainClock, trainPlan,
  type Train,
} from '../lib/trainPlan'
import { useRegionPlace, type RegionPlace } from '../lib/currentRegion'
import { clockIn } from '../lib/zone'
import './TrainsCard.css'

/** An active alert on the line, already worded: Today picks it from `news` by
 *  the same `onMyLine`/`affectsCityToday` the City lane ranks by. */
export interface TrainsAlert { title: string; meta: string | null; onPress: () => void }

const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: TRAIN_ZONE })

/**
 * The next three Fitchburg Line trains each way from the place's stop, the last
 * train in the footer, and an alert on the line when there is one (C4.2).
 *
 * Two columns, one per direction, each row `17:25 → 18:11`: when the train
 * leaves the end it starts and when it gets to the other. Inbound the first
 * time is the stop's own and the second is the run time added; outbound the
 * second is the stop's and the first is the run time taken off — so the time
 * that is the MBTA's is the one on the stop's side, and the other carries a
 * tilde: `~18:11`, `~23:50`, an estimate by a measured run time.
 *
 * A live train carries a dot, and a minute count when it is two or more late.
 * An answer that is not live — the schedule only, or too old to trust —
 * is tagged "schedule" and draws no dots, so it can never pass for a promise.
 * No answer at all (never fetched, no network, nothing persisted) draws
 * nothing, after drawing a placeholder the card's size while it waits so the
 * page below does not jump.
 */
export function TrainsCard({ place, active, alert }: {
  place: Pick<RegionPlace, 'transit_stop' | 'label'>
  /** Today is the visible tab: polling pauses when it is not. */
  active: boolean
  alert: TrainsAlert | null
}) {
  const q = useTrains(place, { active })
  const info = place.transit_stop ? TRAIN_STOPS.get(place.transit_stop) : undefined
  if (!info) return null
  if (q.isPending) {
    return (
      <Card className="trains trains--waiting" aria-busy="true">
        <Skeleton variant="lines" count={1} />
      </Card>
    )
  }
  if (!q.data) return null

  const now = new Date()
  const view = cardView(q.data.trains, now)
  const live = isLive(q.data, now)
  const today = serviceDay(now)
  // Nothing at all to say — no trains in the answer and no alert — is a
  // response from the wrong day or an empty schedule; a card of dashes is worse.
  if (!view.toBoston.length && !view.fromBoston.length && !alert) return null

  const name = place.label?.trim() || info.name
  return (
    <Card className="trains" aria-label={`Trains from ${name}`}>
      <div className="trains__head">
        <h2 className="trains__title">{name} · {info.line}</h2>
        {!live && <span className="trains__tag">schedule</span>}
      </div>
      <div className="trains__cols">
        <Column heading="To North Station" trains={view.toBoston} today={today} live={live} />
        <Column heading="From North Station" trains={view.fromBoston} today={today} live={live} />
      </div>
      {view.footer.length > 0 && (
        <div className="trains__foot">
          {view.footer.map(line => <p key={line} className={view.over && line === view.footer[0] ? 'trains__over' : undefined}>{line}</p>)}
        </div>
      )}
      {alert && (
        <Row
          className="cityrow trains__alert" leading={null}
          title={alert.title} meta={alert.meta} onPress={alert.onPress}
        />
      )}
    </Card>
  )
}

function Column({ heading, trains, today, live }: { heading: string; trains: Train[]; today: string; live: boolean }) {
  return (
    <div className="trains__col">
      <p className="trains__colhead">{heading}</p>
      {trains.length === 0
        ? <p className="trains__none">None</p>
        : (
          // A weekday column only where one train in the column needs it, so
          // the times stay in line (tonight's 23:45 over tomorrow's 05:50).
          <ul className={`trains__list${trains.some(t => serviceDay(atStop(t)) !== today) ? ' has-day' : ''}`}>
            {trains.map(t => <TrainRow key={t.departs + t.direction} t={t} today={today} live={live} />)}
          </ul>
        )}
    </div>
  )
}

function TrainRow({ t, today, live }: { t: Train; today: string; live: boolean }) {
  // The weekday only on a train of another service day than the one it is
  // (tonight's 23:45 then tomorrow's 05:50): 00:29 on the same night is not
  // "Sat" — that is the day-boundary the service day exists to keep.
  const other = serviceDay(atStop(t)) !== today
  const late = live ? lateWord(t) : null
  // A live train is marked by a dot, or by its minutes late when it is late:
  // the amber "+6" says live as well as late, and two marks side by side do
  // not fit a 360px column.
  const dot = live && t.live && !late
  return (
    <li className="trains__row">
      <span className="trains__day">{other ? weekday.format(new Date(Date.parse(t.departs))) : ''}</span>
      <span className="trains__t">{t.direction === 'out' ? '~' : ''}{trainClock(t.departs)}</span>
      <span className="trains__arr" aria-hidden="true">→ {t.direction === 'in' ? '~' : ''}{trainClock(t.arrives)}</span>
      <span className="u-sr">, arrives {trainClock(t.arrives)}</span>
      {dot && <i className="trains__dot" aria-hidden="true" />}
      {dot && <span className="u-sr">, live</span>}
      {late && <span className="trains__late">{late}<span className="u-sr"> minutes late, live</span></span>}
    </li>
  )
}

/** The farthest ahead the Worker will read a schedule (`transitQueryError`). */
const SCHEDULE_AHEAD_DAYS = 119

/**
 * The event sheet's train line: `17:25 from Concord · last train back 23:50
 * from North Station`, and under it the part that is an estimate. Drawn as a
 * `dt`/`dd` pair of the sheet's fact list, or as nothing: not in a region
 * whose place has a stop, not for a venue the line does not serve (Lexington,
 * or Berlin), not for a night with no clock (midnight means untimed), a past
 * night, or one the Worker will not read a schedule for.
 *
 * Tonight's events share the card's own query (`'now'`, with predictions);
 * any other day asks for that service day's schedule, nothing live.
 */
export function TrainPlanLine({ start, venue }: {
  start: string | null
  venue: { lat: number | null; lng: number | null }
}) {
  const place = useRegionPlace()
  const startMs = start ? Date.parse(start) : NaN
  const nowMs = Date.now()
  const coords = venue.lat != null && venue.lng != null ? { lat: venue.lat, lng: venue.lng } : null
  const serves = !!place?.transit_stop && !!coords && reachesFromNorthStation(coords)
    && Number.isFinite(startMs) && startMs > nowMs && startMs - nowMs < SCHEDULE_AHEAD_DAYS * 86_400_000
    && clockIn(new Date(startMs), TRAIN_ZONE) !== '00:00'
  const day = serves ? serviceDay(startMs) : null
  const q = useTrains(serves ? place : null, { date: day === serviceDay(nowMs) ? null : day })
  if (!serves || !coords || !q.data || !place?.transit_stop) return null

  const plan = trainPlan(start!, coords, q.data.trains, new Date(nowMs).toISOString())
  const from = place.label?.trim() || TRAIN_STOPS.get(place.transit_stop)?.name || 'the stop'
  const gap = serviceGap(q.data.trains, 'out', serviceDay(startMs))
  const line = planLines(plan, from, gap && Date.parse(gap.to) > startMs ? gap : null)
  if (!line) return null
  return (
    <>
      <dt>Train</dt>
      <dd>
        {line.text}
        {line.note && <span className="trains__note">{line.note}</span>}
      </dd>
    </>
  )
}
